import i18n from '@/i18n';
import React from 'react';
import { showToast } from '@/utils/toast';
import {
  buildWorkbenchNavUrl,
  navigateWorkbenchWithState,
  TOOL_NAV_ITEMS,
} from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import { getAiStudioHistoryConfig } from '@/pages/ipe/workbench/utils/aiStudioTaskHistory';
import { navigateAfterPlatformListingIngest } from '@/pages/ipe/workbench/utils/ingestDetailBridgeSourcesToPlatformListing';

export const TASK_CREATED_DETAIL_TOAST_MS = 5000;

/** 历史角标 scope：功能 id（如 img2img）；存储 key 为 scope:workspaceMode */
export function resolveFeatureBadgeScopeId(navId) {
  const id = String(navId || '').trim();
  if (id === 'goods') return 'goodsStudio';
  if (id === 'podProductDesign') return 'podBatch';
  if (id === 'infringementDetection') return 'infringementDetection';
  const config = getAiStudioHistoryConfig(id);
  return config?.badgeScopeId || id;
}

export function resolveFeatureLabel(navId) {
  const id = String(navId || '').trim();
  return TOOL_NAV_ITEMS[id]?.label
    || getAiStudioHistoryConfig(id)?.toolLabel
    || '任务';
}

/**
 * 任务创建成功提示：点击「功能名称」跳转对应功能页（不进详情、不打开历史、不清角标）。
 * 文案：任务创建成功，点击{{功能名称}}详情进行查看；默认展示 5 秒。
 * 标准流程：创建 → 角标+1 → 历史记录选详情 → 进入详情 → 清角标。
 */
export function showTaskCreatedDetailToast({
  navId,
  taskId,
  navigate,
  featureLabel,
  durationMs = TASK_CREATED_DETAIL_TOAST_MS,
} = {}) {
  const id = String(navId || '').trim();
  const key = String(taskId || '').trim();
  if (!id || !key) return null;

  const label = featureLabel || resolveFeatureLabel(id);
  const featurePath = buildWorkbenchNavUrl(id);

  let closer = null;
  const message = (
    <span className="ipe-task-created-toast">
      任务创建成功，点击
      <button
        type="button"
        className="ipe-task-created-toast__link"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closer?.();
          if (typeof navigate === 'function') {
            navigateWorkbenchWithState(navigate, id);
          } else if (featurePath) {
            window.location.assign(featurePath);
          }
        }}
      >
        {label}
      </button>
      详情进行查看
    </span>
  );

  const handle = showToast(message, 'success', durationMs);
  closer = handle?.close || null;
  return handle;
}

/**
 * 快捷功能确认后的统一成功反馈（有 taskId 则出可点功能名 Toast，否则普通成功提示）。
 */
export function notifyQuickBridgeTaskCreated({
  created,
  fallbackNavId,
  navigate,
  featureLabel,
} = {}) {
  const navId = String(created?.navId || fallbackNavId || '').trim();
  const taskId = String(created?.taskId || '').trim();
  if (navId && taskId) {
    return showTaskCreatedDetailToast({
      navId,
      taskId,
      navigate,
      featureLabel,
    });
  }
  return showToast(i18n.t('workbench:ipeUtilError.taskCreated'), 'success');
}

/**
 * 详情快捷写入商品库：批次已创建、后台入库中；点击可立即查看进度。
 */
export function showPlatformListingIngestStartedToast({
  navigate,
  batchKey,
  recolor = false,
  durationMs = TASK_CREATED_DETAIL_TOAST_MS,
} = {}) {
  const label = TOOL_NAV_ITEMS.platformListing?.label || '本地商品库';

  let closer = null;
  const message = (
    <span className="ipe-task-created-toast">
      {recolor ? '入库任务已创建，完成后点击' : '入库任务已创建，点击'}
      <button
        type="button"
        className="ipe-task-created-toast__link"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closer?.();
          navigateAfterPlatformListingIngest({
            navigate,
            batchKey,
            recolor,
          });
        }}
      >
        {label}
      </button>
      {recolor ? '进行 SPU 复色' : '查看进度'}
    </span>
  );

  const handle = showToast(message, 'success', durationMs);
  closer = handle?.close || null;
  return handle;
}

/**
 * 详情快捷写入商品库成功提示：不自动跳转；点击「商品库」再进入列表/单条详情。
 * 与 {@link showTaskCreatedDetailToast} 同款可点功能名 Toast。
 */
export function showPlatformListingIngestToast({
  navigate,
  batchKey,
  ingestedRows = [],
  count = 0,
  durationMs = TASK_CREATED_DETAIL_TOAST_MS,
} = {}) {
  const n = Number(count) || 0;
  const label = TOOL_NAV_ITEMS.platformListing?.label || '本地商品库';
  const prefix = n > 1 ? `已保存 ${n} 条商品，点击` : '已保存到商品库，点击';

  let closer = null;
  const message = (
    <span className="ipe-task-created-toast">
      {prefix}
      <button
        type="button"
        className="ipe-task-created-toast__link"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closer?.();
          navigateAfterPlatformListingIngest({
            navigate,
            batchKey,
            ingestedRows,
            count: n,
          });
        }}
      >
        {label}
      </button>
      查看
    </span>
  );

  const handle = showToast(message, 'success', durationMs);
  closer = handle?.close || null;
  return handle;
}
